// src/lib/phoneOtp.js
//
// Robust, device-agnostic Firebase phone OTP sender.
//
// Why this exists (root causes of auth/captcha-check-failed on mobile):
//  - A long-lived, visible reCAPTCHA widget kept in React state goes stale on mobile
//    (tab backgrounded, >2 min token lifetime, re-renders, double taps re-using a consumed token).
//  - RecaptchaVerifier.verify() never resolves if the user dismisses the image challenge.
//  - Server-side rejections ("Hostname match not found", expired token...) were invisible to us.
//
// Strategy:
//  - Create a brand-new INVISIBLE RecaptchaVerifier for every attempt, in a fresh DOM node,
//    so the token is generated right before it is consumed and is never reused.
//  - Automatically retry once with a new verifier on retryable failures.
//  - Hard timeout so the UI never hangs.
//  - Log every failure (code, server message, browser env) to Firestore for diagnosis.

import { getAuth, RecaptchaVerifier, PhoneAuthProvider } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getBrowserEnv } from './recaptchaUtils';

const MAX_ATTEMPTS = 2;
const ATTEMPT_TIMEOUT_MS = 120000; // user may need to solve an image challenge

const RETRYABLE_CODES = new Set([
    'auth/captcha-check-failed',
    'auth/internal-error',
    'auth/network-request-failed',
    'auth/invalid-app-credential',
    'auth/missing-app-credential',
    'app/recaptcha-timeout',
]);

function makeTimeoutError() {
    const err = new Error('Güvenlik doğrulaması zaman aşımına uğradı.');
    err.code = 'app/recaptcha-timeout';
    return err;
}

function maskPhone(e164) {
    if (!e164) return '';
    return e164.slice(0, 4) + '*****' + e164.slice(-2);
}

/**
 * Fire-and-forget diagnostic log. Never throws.
 */
export function logPhoneAuthEvent(type, data = {}) {
    try {
        const auth = getAuth();
        if (!auth.currentUser) return; // rules require auth
        const env = getBrowserEnv();
        addDoc(collection(db, 'phoneAuthLogs'), {
            type,
            uid: auth.currentUser.uid,
            host: window.location.hostname,
            path: window.location.pathname,
            userAgent: navigator.userAgent || '',
            browser: env.name,
            isInApp: env.isInApp,
            isAndroid: env.isAndroid,
            isIOS: env.isIOS,
            online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
            cookiesEnabled: typeof navigator.cookieEnabled === 'boolean' ? navigator.cookieEnabled : null,
            createdAt: serverTimestamp(),
            ...data,
        }).catch(() => { /* ignore logging failures */ });
    } catch (_) {
        /* ignore */
    }
}

/**
 * Sends an SMS verification code and returns the verificationId.
 *
 * @param {object}   opts
 * @param {string}   opts.phoneNumber  E.164 phone number (+905xxxxxxxxx)
 * @param {HTMLElement} opts.host      Element that will host the (inline) reCAPTCHA badge
 * @param {function} [opts.onAttempt]  Called with attempt number before each attempt
 * @returns {Promise<string>} verificationId
 */
export async function requestPhoneOtp({ phoneNumber, host, onAttempt }) {
    if (!host) {
        const err = new Error('reCAPTCHA container not found');
        err.code = 'auth/internal-error';
        throw err;
    }

    const auth = getAuth();
    try {
        auth.languageCode = 'tr';
    } catch (_) {
        try { auth.useDeviceLanguage(); } catch (_) { /* ignore */ }
    }

    let lastError = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (onAttempt) onAttempt(attempt);

        // Fresh DOM node every attempt -> no "already rendered" or stale widget state.
        host.innerHTML = '';
        const node = document.createElement('div');
        host.appendChild(node);

        let verifier = null;
        let timer = null;

        try {
            verifier = new RecaptchaVerifier(auth, node, {
                size: 'invisible',
                badge: 'inline',
            });

            const provider = new PhoneAuthProvider(auth);
            const timeout = new Promise((_, reject) => {
                timer = setTimeout(() => reject(makeTimeoutError()), ATTEMPT_TIMEOUT_MS);
            });

            const verificationId = await Promise.race([
                provider.verifyPhoneNumber(phoneNumber, verifier),
                timeout,
            ]);

            if (attempt > 1) {
                logPhoneAuthEvent('otp_sent_after_retry', { attempt, phone: maskPhone(phoneNumber) });
            }
            return verificationId;
        } catch (err) {
            lastError = err;
            logPhoneAuthEvent('otp_send_error', {
                attempt,
                phone: maskPhone(phoneNumber),
                code: err?.code || 'unknown',
                message: String(err?.message || ''),
                serverResponse: err?.customData?._serverResponse
                    ? JSON.stringify(err.customData._serverResponse).slice(0, 1000)
                    : null,
            });

            if (!RETRYABLE_CODES.has(err?.code) || attempt === MAX_ATTEMPTS) {
                break;
            }
            // Short pause before retrying with a brand new verifier
            await new Promise((r) => setTimeout(r, 600));
        } finally {
            if (timer) clearTimeout(timer);
            if (verifier) {
                try { verifier.clear(); } catch (_) { /* ignore */ }
            }
            try { host.innerHTML = ''; } catch (_) { /* ignore */ }
        }
    }

    throw lastError;
}
